/**
 * NEMSIS v3.5-aligned clinical reference lists.
 *
 * Sources:
 *   - NEMSIS v3.5.1 controlled vocabulary (eSituation, ePatient, eExam)
 *   - ICD-10 EMS short-list (most-frequent ePCR impressions)
 *   - RIEMSIS field use + common EMS documentation practice
 *
 * Phrasing conventions (applied uniformly across every list):
 *   - Title Case for concept names ("Chest Pain", not "chest pain" or "CHEST PAIN")
 *   - Abbreviations in parentheses when first introduced ("Myocardial Infarction (MI)")
 *   - No punctuation at end of entries
 *   - Symptoms phrased from the patient's perspective ("Shortness of Breath")
 *   - Signs phrased from the provider's observation ("Diaphoresis")
 *   - Each list ends with an "Other (document below)" and "Unknown" pair where appropriate
 *
 * All lists are de-duplicated and sorted inside their subcategory so the
 * SearchableSelect's virtualized dropdown stays predictable. A grouped
 * shape (category → options) is used for lists over ~50 entries so the
 * user can collapse sections when scanning.
 */

// ============================================================
// SHARED SHAPES
// ============================================================

/**
 * Pharmacologic / clinical tags applied to medication + allergy entries.
 * Tags drive decision-support: a patient on an anticoagulant needs
 * a red-flag banner; a patient on insulin is a glucose-check prompt;
 * a patient on an opioid is a naloxone-ready prompt.
 *
 * Aspirin is DELIBERATELY not flagged 'anticoagulant' or 'antiplatelet'
 * at the app level because it's ubiquitous and the risk/benefit of
 * flagging it on every patient > 50 is noise. Real clopidogrel /
 * apixaban / warfarin / enoxaparin get the flag.
 */
export type ClinicalFlag =
  | 'anticoagulant'      // warfarin, DOACs, heparin, LMWH, fondaparinux
  | 'antiplatelet'       // clopidogrel, ticagrelor, prasugrel (NOT aspirin)
  | 'insulin'            // any insulin preparation
  | 'opioid'             // any narcotic analgesic
  | 'benzodiazepine'     // sedative/anxiolytic
  | 'beta_blocker'       // masks hypoglycemia + shock tachycardia
  | 'immunosuppressant'  // chronic steroid / biologic / transplant rx
  | 'qt_prolonging'      // meds that prolong QT
  | 'controlled_2'       // Schedule II — documentation implications
  | 'maoi';              // drug-interaction nightmare

export interface ClinicalOption {
  /** Stable canonical label shown to the user and stored on the record. */
  label: string;
  /** Alternate names / nicknames that should match in search (SOB → Shortness of Breath). */
  aliases?: string[];
  /** Optional NEMSIS/ICD-10 code. Useful for downstream coded export. */
  code?: string;
  /** Decision-support tags. Downstream components read these to show
   *  alerts, prompts, and guidance. */
  flags?: ClinicalFlag[];
}

export interface ClinicalGroup {
  category: string;
  options: ClinicalOption[];
}

// ============================================================
// PATIENT CHIEF COMPLAINTS  (eSituation.09 / NEMSIS 3.5)
// ============================================================
// Patient-stated reason for call. These are the most common EMS
// complaints; providers can always free-text anything off-list.

export const CHIEF_COMPLAINTS: ClinicalGroup[] = [
  {
    category: 'Cardiovascular',
    options: [
      { label: 'Chest Pain' },
      { label: 'Chest Pressure' },
      { label: 'Chest Tightness' },
      { label: 'Palpitations' },
      { label: 'Syncope' },
      { label: 'Near-Syncope' },
      { label: 'Lightheadedness' },
      { label: 'Implantable Defibrillator Fired' },
    ],
  },
  {
    category: 'Respiratory',
    options: [
      { label: 'Shortness of Breath', aliases: ['SOB', 'Dyspnea'] },
      { label: 'Difficulty Breathing' },
      { label: 'Wheezing' },
      { label: 'Cough' },
      { label: 'Coughing Up Blood', aliases: ['Hemoptysis'] },
      { label: 'Choking' },
      { label: 'Respiratory Arrest' },
    ],
  },
  {
    category: 'Neurological',
    options: [
      { label: 'Altered Mental Status', aliases: ['AMS'] },
      { label: 'Unresponsive' },
      { label: 'Weakness (One-Sided)' },
      { label: 'Weakness (Generalized)' },
      { label: 'Numbness / Tingling' },
      { label: 'Facial Droop' },
      { label: 'Slurred Speech' },
      { label: 'Headache' },
      { label: 'Severe Headache (Worst of Life)' },
      { label: 'Seizure (Active)' },
      { label: 'Seizure (Post-Ictal)' },
      { label: 'Dizziness / Vertigo' },
      { label: 'Confusion' },
      { label: 'Stroke Symptoms', aliases: ['CVA', 'LVO'] },
      { label: 'Loss of Consciousness' },
    ],
  },
  {
    category: 'Gastrointestinal',
    options: [
      { label: 'Abdominal Pain' },
      { label: 'Nausea' },
      { label: 'Vomiting' },
      { label: 'Vomiting Blood', aliases: ['Hematemesis'] },
      { label: 'Diarrhea' },
      { label: 'Constipation' },
      { label: 'Rectal Bleeding' },
      { label: 'Black / Tarry Stool', aliases: ['Melena'] },
      { label: 'Inability to Swallow' },
    ],
  },
  {
    category: 'Genitourinary',
    options: [
      { label: 'Flank Pain' },
      { label: 'Urinary Retention' },
      { label: 'Dysuria' },
      { label: 'Hematuria' },
      { label: 'Vaginal Bleeding' },
      { label: 'Active Labor' },
      { label: 'Pregnancy-Related Emergency' },
      { label: 'Testicular Pain' },
    ],
  },
  {
    category: 'Trauma',
    options: [
      { label: 'Motor Vehicle Collision (MVC)' },
      { label: 'Motorcycle Collision' },
      { label: 'Pedestrian Struck' },
      { label: 'Bicycle Collision' },
      { label: 'Fall (Ground Level)' },
      { label: 'Fall (From Height)' },
      { label: 'Fall (Stairs)' },
      { label: 'Assault' },
      { label: 'Gunshot Wound (GSW)' },
      { label: 'Stabbing' },
      { label: 'Penetrating Trauma' },
      { label: 'Blunt Trauma' },
      { label: 'Burn (Thermal)' },
      { label: 'Burn (Chemical)' },
      { label: 'Burn (Electrical)' },
      { label: 'Smoke Inhalation' },
      { label: 'Laceration' },
      { label: 'Amputation' },
      { label: 'Crush Injury' },
      { label: 'Degloving Injury' },
      { label: 'Sports Injury' },
      { label: 'Industrial / Workplace Injury' },
    ],
  },
  {
    category: 'Environmental / Toxicologic',
    options: [
      { label: 'Overdose (Suspected Opioid)' },
      { label: 'Overdose (Polysubstance)' },
      { label: 'Overdose (Intentional)' },
      { label: 'Overdose (Accidental)' },
      { label: 'Alcohol Intoxication' },
      { label: 'Poisoning / Ingestion' },
      { label: 'Carbon Monoxide Exposure' },
      { label: 'Hazmat Exposure' },
      { label: 'Hypothermia' },
      { label: 'Hyperthermia / Heat Illness' },
      { label: 'Electrocution' },
      { label: 'Drowning / Near-Drowning' },
      { label: 'Lightning Strike' },
      { label: 'Bee / Wasp Sting' },
      { label: 'Animal Bite' },
      { label: 'Snake Bite' },
    ],
  },
  {
    category: 'Behavioral / Psychiatric',
    options: [
      { label: 'Suicidal Ideation' },
      { label: 'Suicide Attempt' },
      { label: 'Self-Harm' },
      { label: 'Homicidal Ideation' },
      { label: 'Anxiety / Panic Attack' },
      { label: 'Psychiatric Emergency' },
      { label: 'Emotional Distress' },
      { label: 'Hallucinations' },
      { label: 'Combative Behavior' },
    ],
  },
  {
    category: 'Pain / Musculoskeletal',
    options: [
      { label: 'Back Pain' },
      { label: 'Neck Pain' },
      { label: 'Joint Pain' },
      { label: 'Extremity Pain' },
      { label: 'Extremity Injury (Suspected Fracture)' },
      { label: 'Dislocation' },
      { label: 'Sprain / Strain' },
    ],
  },
  {
    category: 'Endocrine / Metabolic',
    options: [
      { label: 'Hypoglycemia (Low Blood Sugar)' },
      { label: 'Hyperglycemia (High Blood Sugar)' },
      { label: 'Diabetic Emergency (Unspecified)' },
      { label: 'Electrolyte Abnormality' },
    ],
  },
  {
    category: 'Infection / Systemic',
    options: [
      { label: 'Fever' },
      { label: 'Sepsis (Suspected)' },
      { label: 'Flu-Like Symptoms' },
      { label: 'COVID-19 Symptoms' },
      { label: 'Allergic Reaction' },
      { label: 'Anaphylaxis' },
    ],
  },
  {
    category: 'Obstetric / Pediatric',
    options: [
      { label: 'Active Labor' },
      { label: 'Imminent Delivery' },
      { label: 'Newborn Care' },
      { label: 'Umbilical Cord Prolapse' },
      { label: 'Shoulder Dystocia' },
      { label: 'Postpartum Hemorrhage' },
      { label: 'Placental Abruption (Suspected)' },
      { label: 'Placenta Previa (Suspected)' },
      { label: 'Miscarriage' },
      { label: 'Sexual Assault' },
      { label: 'Pediatric Fever' },
      { label: 'Pediatric Respiratory Distress' },
      { label: 'Pediatric Seizure' },
      { label: 'Pediatric Dehydration' },
      { label: 'Pediatric Altered Mental Status' },
      { label: 'Pediatric Foreign Body Obstruction' },
      { label: 'Apparent Life-Threatening Event (ALTE / BRUE)' },
      { label: 'Unresponsive Infant' },
      { label: 'Child with Special Healthcare Needs' },
      { label: 'Suspected Child Abuse / Neglect' },
      { label: 'Failure to Thrive' },
    ],
  },
  {
    category: 'Other',
    options: [
      { label: 'Bleeding (External, Controlled)' },
      { label: 'Bleeding (External, Uncontrolled)' },
      { label: 'General Weakness / Malaise' },
      { label: 'Medical Alarm Activation' },
      { label: 'Lift Assist / No Injury' },
      { label: 'Welfare Check' },
      { label: 'Cardiac Arrest' },
      { label: 'Death on Arrival (DOA)' },
      { label: 'Other (Document in Narrative)' },
      { label: 'Unknown' },
    ],
  },
];

// ============================================================
// PROVIDER PRIMARY IMPRESSIONS  (eSituation.11 / NEMSIS 3.5)
// ============================================================
// The provider's working diagnosis at the time of care. Maps directly
// to NEMSIS eSituation.11 PrimaryImpression code list (v3.5).

export const PROVIDER_IMPRESSIONS: ClinicalGroup[] = [
  {
    category: 'Cardiovascular — Medical',
    options: [
      { label: 'Acute Coronary Syndrome (ACS)', code: '2301001' },
      { label: 'ST-Elevation Myocardial Infarction (STEMI)', code: '2301002' },
      { label: 'Non-STEMI / NSTEMI', code: '2301003' },
      { label: 'Unstable Angina', code: '2301004' },
      { label: 'Stable Angina' },
      { label: 'Chest Pain — Non-Cardiac' },
      { label: 'Chest Pain — Etiology Undetermined' },
      { label: 'Cardiac Arrest — Medical' },
      { label: 'Cardiac Arrest — Return of Spontaneous Circulation (ROSC)' },
      { label: 'Cardiac Dysrhythmia (Bradycardia)' },
      { label: 'Cardiac Dysrhythmia (Tachycardia — Narrow)' },
      { label: 'Cardiac Dysrhythmia (Tachycardia — Wide)' },
      { label: 'Atrial Fibrillation with RVR' },
      { label: 'Supraventricular Tachycardia (SVT)' },
      { label: 'Ventricular Tachycardia (VT)' },
      { label: 'Ventricular Fibrillation (VF)' },
      { label: 'Pulseless Electrical Activity (PEA)' },
      { label: 'Asystole' },
      { label: 'Hypertensive Emergency' },
      { label: 'Hypertension (Asymptomatic)' },
      { label: 'Hypotension / Shock — Cardiogenic' },
      { label: 'Congestive Heart Failure (CHF) Exacerbation' },
      { label: 'Pulmonary Edema (Cardiogenic)' },
      { label: 'Aortic Dissection (Suspected)' },
      { label: 'Pericarditis' },
      { label: 'Syncope / Near-Syncope' },
    ],
  },
  {
    category: 'Respiratory — Medical',
    options: [
      { label: 'Respiratory Distress' },
      { label: 'Respiratory Failure' },
      { label: 'Respiratory Arrest' },
      { label: 'Asthma Exacerbation' },
      { label: 'COPD Exacerbation' },
      { label: 'Pneumonia (Suspected)' },
      { label: 'Pulmonary Embolism (Suspected)' },
      { label: 'Pleural Effusion' },
      { label: 'Pneumothorax (Spontaneous)' },
      { label: 'Upper Airway Obstruction' },
      { label: 'Epiglottitis / Croup (Pediatric)' },
      { label: 'Hyperventilation Syndrome' },
      { label: 'Hypoxia' },
    ],
  },
  {
    category: 'Neurological — Medical',
    options: [
      { label: 'Stroke / CVA (Acute)' },
      { label: 'Large Vessel Occlusion (LVO) Stroke' },
      { label: 'Transient Ischemic Attack (TIA)' },
      { label: 'Intracranial Hemorrhage (Suspected)' },
      { label: 'Subarachnoid Hemorrhage (Suspected)' },
      { label: 'Seizure — Generalized' },
      { label: 'Seizure — Focal' },
      { label: 'Status Epilepticus' },
      { label: 'Post-Ictal State' },
      { label: 'Syncope — Neurologic Cause' },
      { label: 'Altered Mental Status (Cause Unknown)' },
      { label: 'Headache — Primary' },
      { label: 'Migraine' },
      { label: 'Vertigo — Peripheral' },
      { label: 'Vertigo — Central' },
      { label: 'Bell Palsy' },
      { label: 'Spinal Cord Syndrome' },
    ],
  },
  {
    category: 'Gastrointestinal — Medical',
    options: [
      { label: 'Abdominal Pain — Etiology Undetermined' },
      { label: 'GI Bleeding (Upper)' },
      { label: 'GI Bleeding (Lower)' },
      { label: 'Gastroenteritis' },
      { label: 'Bowel Obstruction (Suspected)' },
      { label: 'Appendicitis (Suspected)' },
      { label: 'Cholecystitis / Biliary Colic' },
      { label: 'Pancreatitis' },
      { label: 'Hepatic Failure' },
      { label: 'Esophageal Varices (Bleeding)' },
      { label: 'Nausea / Vomiting' },
      { label: 'Constipation / Fecal Impaction' },
    ],
  },
  {
    category: 'Endocrine / Metabolic',
    options: [
      { label: 'Hypoglycemia' },
      { label: 'Hyperglycemia (Non-Ketotic)' },
      { label: 'Diabetic Ketoacidosis (DKA)' },
      { label: 'Hyperosmolar Hyperglycemic State (HHS)' },
      { label: 'Electrolyte Disturbance' },
      { label: 'Dehydration' },
      { label: 'Adrenal Crisis' },
      { label: 'Thyroid Storm' },
      { label: 'Myxedema Coma' },
    ],
  },
  {
    category: 'Genitourinary / Renal',
    options: [
      { label: 'Renal Colic / Kidney Stone' },
      { label: 'Urinary Tract Infection (UTI)' },
      { label: 'Acute Kidney Injury' },
      { label: 'Dialysis Complication' },
      { label: 'Urinary Retention' },
      { label: 'Pyelonephritis' },
      { label: 'Testicular Torsion (Suspected)' },
    ],
  },
  {
    category: 'Infection / Immune',
    options: [
      { label: 'Sepsis' },
      { label: 'Septic Shock' },
      { label: 'Fever of Unknown Origin' },
      { label: 'Cellulitis / Skin Infection' },
      { label: 'Meningitis (Suspected)' },
      { label: 'Allergic Reaction — Localized' },
      { label: 'Allergic Reaction — Systemic' },
      { label: 'Anaphylaxis' },
      { label: 'Angioedema' },
    ],
  },
  {
    category: 'Behavioral / Psychiatric',
    options: [
      { label: 'Psychiatric Emergency — Unspecified' },
      { label: 'Suicidal Ideation' },
      { label: 'Suicide Attempt' },
      { label: 'Homicidal Ideation' },
      { label: 'Anxiety / Panic Attack' },
      { label: 'Acute Psychosis' },
      { label: 'Agitation / Excited Delirium' },
      { label: 'Self-Harm' },
      { label: 'Substance-Induced Psychosis' },
    ],
  },
  {
    category: 'Toxicologic',
    options: [
      { label: 'Opioid Overdose' },
      { label: 'Polysubstance Overdose' },
      { label: 'Stimulant Overdose' },
      { label: 'Benzodiazepine Overdose' },
      { label: 'Alcohol Intoxication' },
      { label: 'Alcohol Withdrawal' },
      { label: 'Opioid Withdrawal' },
      { label: 'Carbon Monoxide Poisoning' },
      { label: 'Cyanide Poisoning' },
      { label: 'Organophosphate Poisoning' },
      { label: 'Acetaminophen Toxicity' },
      { label: 'Salicylate Toxicity' },
      { label: 'Poisoning / Ingestion — Unknown Substance' },
    ],
  },
  {
    category: 'Environmental',
    options: [
      { label: 'Hypothermia' },
      { label: 'Frostbite' },
      { label: 'Heat Exhaustion' },
      { label: 'Heat Stroke' },
      { label: 'Drowning / Submersion Injury' },
      { label: 'Electrocution' },
      { label: 'Lightning Strike' },
      { label: 'Envenomation — Insect' },
      { label: 'Envenomation — Snake' },
      { label: 'Marine Envenomation' },
      { label: 'Altitude Sickness' },
      { label: 'Decompression Sickness' },
      { label: 'Smoke Inhalation' },
    ],
  },
  {
    category: 'Trauma — Head / Neck / Face',
    options: [
      { label: 'Traumatic Brain Injury — Mild' },
      { label: 'Traumatic Brain Injury — Moderate' },
      { label: 'Traumatic Brain Injury — Severe' },
      { label: 'Concussion' },
      { label: 'Skull Fracture (Suspected)' },
      { label: 'Facial Trauma' },
      { label: 'Dental Trauma' },
      { label: 'Ocular Injury' },
      { label: 'Cervical Spine Injury (Suspected)' },
      { label: 'Airway Trauma' },
      { label: 'Neck Hematoma / Trauma' },
    ],
  },
  {
    category: 'Trauma — Thorax',
    options: [
      { label: 'Chest Trauma — Blunt' },
      { label: 'Chest Trauma — Penetrating' },
      { label: 'Rib Fracture(s)' },
      { label: 'Flail Chest' },
      { label: 'Pneumothorax (Traumatic)' },
      { label: 'Tension Pneumothorax' },
      { label: 'Hemothorax' },
      { label: 'Pulmonary Contusion' },
      { label: 'Cardiac Tamponade (Traumatic)' },
      { label: 'Sternal Fracture' },
    ],
  },
  {
    category: 'Trauma — Abdomen / Pelvis',
    options: [
      { label: 'Abdominal Trauma — Blunt' },
      { label: 'Abdominal Trauma — Penetrating' },
      { label: 'Pelvic Fracture (Suspected)' },
      { label: 'Genitourinary Trauma' },
      { label: 'Retroperitoneal Hemorrhage (Suspected)' },
      { label: 'Evisceration' },
    ],
  },
  {
    category: 'Trauma — Spine',
    options: [
      { label: 'Cervical Spine Injury' },
      { label: 'Thoracic Spine Injury' },
      { label: 'Lumbar Spine Injury' },
      { label: 'Spinal Cord Injury' },
    ],
  },
  {
    category: 'Trauma — Extremity',
    options: [
      { label: 'Upper Extremity Fracture (Closed)' },
      { label: 'Upper Extremity Fracture (Open)' },
      { label: 'Lower Extremity Fracture (Closed)' },
      { label: 'Lower Extremity Fracture (Open)' },
      { label: 'Joint Dislocation' },
      { label: 'Amputation — Traumatic' },
      { label: 'Crush Injury' },
      { label: 'Compartment Syndrome (Suspected)' },
      { label: 'Sprain / Strain' },
      { label: 'Laceration — Minor' },
      { label: 'Laceration — Major / Hemorrhagic' },
      { label: 'Degloving Injury' },
      { label: 'Puncture Wound' },
    ],
  },
  {
    category: 'Trauma — Burn',
    options: [
      { label: 'Burn — Thermal — 1st Degree' },
      { label: 'Burn — Thermal — 2nd Degree' },
      { label: 'Burn — Thermal — 3rd Degree' },
      { label: 'Burn — Chemical' },
      { label: 'Burn — Electrical' },
      { label: 'Burn — Inhalation' },
      { label: 'Burn — Friction' },
      { label: 'Burn — Radiation' },
    ],
  },
  {
    category: 'Trauma — General',
    options: [
      { label: 'Multi-System Trauma' },
      { label: 'Traumatic Cardiac Arrest' },
      { label: 'Hemorrhagic Shock' },
      { label: 'Traumatic Asphyxia' },
      { label: 'Blast Injury' },
      { label: 'Traumatic Amputation' },
    ],
  },
  {
    category: 'Obstetric / Gynecologic',
    options: [
      { label: 'Active Labor' },
      { label: 'Imminent Delivery' },
      { label: 'Delivery — Field' },
      { label: 'Post-Partum Hemorrhage' },
      { label: 'Pre-Eclampsia / Eclampsia' },
      { label: 'Ectopic Pregnancy (Suspected)' },
      { label: 'Vaginal Bleeding (Non-OB)' },
      { label: 'Miscarriage / Pregnancy Loss' },
      { label: 'Sexual Assault' },
    ],
  },
  {
    category: 'Pediatric',
    options: [
      { label: 'Pediatric Respiratory Distress' },
      { label: 'Pediatric Fever' },
      { label: 'Pediatric Seizure' },
      { label: 'Pediatric Sepsis' },
      { label: 'Pediatric Dehydration' },
      { label: 'Apparent Life-Threatening Event (ALTE / BRUE)' },
      { label: 'Sudden Infant Death Syndrome (SIDS) — Suspected' },
      { label: 'Pediatric Trauma' },
      { label: 'Child Abuse / Neglect (Suspected)' },
    ],
  },
  {
    category: 'Other',
    options: [
      { label: 'General Weakness / Malaise' },
      { label: 'No Acute Complaint' },
      { label: 'No Patient Contact' },
      { label: 'Patient Refused Assessment' },
      { label: 'Lift Assist — No Injury' },
      { label: 'Standby / Public Service' },
      { label: 'Death — Pronounced in Field' },
      { label: 'Other (Document in Narrative)' },
    ],
  },
];

// ============================================================
// SIGNS  (eExam findings — provider observed)
// ============================================================
export const SIGNS: ClinicalGroup[] = [
  {
    category: 'General Appearance',
    options: [
      { label: 'Awake / Alert' },
      { label: 'Responsive to Voice' },
      { label: 'Responsive to Pain' },
      { label: 'Unresponsive' },
      { label: 'Diaphoretic' },
      { label: 'Pale' },
      { label: 'Flushed' },
      { label: 'Cyanotic' },
      { label: 'Jaundiced' },
      { label: 'Mottled Skin' },
      { label: 'Cool Skin' },
      { label: 'Warm Skin' },
      { label: 'Clammy Skin' },
      { label: 'Fever to Touch' },
      { label: 'Tripod Positioning' },
      { label: 'Agitation / Combativeness' },
    ],
  },
  {
    category: 'Airway / Breathing',
    options: [
      { label: 'Stridor' },
      { label: 'Wheezing (Audible)' },
      { label: 'Wheezing (Auscultated)' },
      { label: 'Rales / Crackles' },
      { label: 'Rhonchi' },
      { label: 'Diminished Breath Sounds (Unilateral)' },
      { label: 'Diminished Breath Sounds (Bilateral)' },
      { label: 'Absent Breath Sounds' },
      { label: 'Retractions' },
      { label: 'Accessory Muscle Use' },
      { label: 'Nasal Flaring' },
      { label: 'Grunting Respirations' },
      { label: 'Apnea' },
      { label: 'Agonal Respirations' },
      { label: 'Cough' },
      { label: 'Pink Frothy Sputum' },
    ],
  },
  {
    category: 'Cardiovascular',
    options: [
      { label: 'Tachycardia' },
      { label: 'Bradycardia' },
      { label: 'Irregular Pulse' },
      { label: 'Weak / Thready Pulse' },
      { label: 'Bounding Pulse' },
      { label: 'Absent Distal Pulse' },
      { label: 'Capillary Refill > 2 Seconds' },
      { label: 'Jugular Venous Distension (JVD)' },
      { label: 'Peripheral Edema' },
      { label: 'Pitting Edema' },
      { label: 'Murmur' },
      { label: 'Hypertension' },
      { label: 'Hypotension' },
      { label: 'Narrow Pulse Pressure' },
      { label: 'Widened Pulse Pressure' },
    ],
  },
  {
    category: 'Neurological',
    options: [
      { label: 'Facial Droop' },
      { label: 'Arm Drift' },
      { label: 'Slurred Speech' },
      { label: 'Aphasia' },
      { label: 'Dysarthria' },
      { label: 'Unequal Pupils' },
      { label: 'Fixed / Dilated Pupils' },
      { label: 'Pinpoint Pupils' },
      { label: 'Sluggish Pupil Response' },
      { label: 'Nystagmus' },
      { label: 'Positive Babinski' },
      { label: 'Hemiparesis' },
      { label: 'Unilateral Weakness' },
      { label: 'Bilateral Weakness' },
      { label: 'Seizure Activity (Tonic-Clonic)' },
      { label: 'Decorticate Posturing' },
      { label: 'Decerebrate Posturing' },
      { label: 'Loss of Gag Reflex' },
    ],
  },
  {
    category: 'GI / GU',
    options: [
      { label: 'Abdominal Tenderness' },
      { label: 'Rebound Tenderness' },
      { label: 'Guarding' },
      { label: 'Rigidity' },
      { label: 'Distension' },
      { label: 'Pulsatile Mass' },
      { label: 'Visible Hematemesis' },
      { label: 'Melena on Exam' },
      { label: 'Bright Red Blood per Rectum' },
      { label: 'Vaginal Bleeding (Active)' },
      { label: 'Crowning' },
    ],
  },
  {
    category: 'Integumentary',
    options: [
      { label: 'Rash — Urticarial' },
      { label: 'Rash — Petechial' },
      { label: 'Rash — Purpuric' },
      { label: 'Laceration' },
      { label: 'Abrasion' },
      { label: 'Avulsion' },
      { label: 'Burn — Superficial' },
      { label: 'Burn — Partial Thickness' },
      { label: 'Burn — Full Thickness' },
      { label: 'Contusion / Ecchymosis' },
      { label: 'Hematoma' },
      { label: 'Deformity (Gross)' },
      { label: 'Crepitus' },
      { label: 'Bleeding — Controlled' },
      { label: 'Bleeding — Uncontrolled' },
      { label: 'Impaled Object' },
    ],
  },
  {
    category: 'Musculoskeletal',
    options: [
      { label: 'Obvious Deformity' },
      { label: 'Open Fracture' },
      { label: 'Dislocation' },
      { label: 'Range of Motion Limited' },
      { label: 'Tenderness on Palpation' },
      { label: 'Pulse / Motor / Sensory Intact Distal' },
      { label: 'Loss of Distal Neurovascular Function' },
    ],
  },
];

// ============================================================
// SYMPTOMS  (patient-reported)
// ============================================================
export const SYMPTOMS: ClinicalGroup[] = [
  {
    category: 'Pain',
    options: [
      { label: 'Chest Pain' },
      { label: 'Chest Pressure' },
      { label: 'Abdominal Pain' },
      { label: 'Back Pain' },
      { label: 'Neck Pain' },
      { label: 'Headache' },
      { label: 'Flank Pain' },
      { label: 'Extremity Pain' },
      { label: 'Joint Pain' },
      { label: 'Facial Pain' },
      { label: 'Dental Pain' },
      { label: 'Throat Pain' },
      { label: 'Eye Pain' },
      { label: 'Ear Pain' },
      { label: 'Pelvic Pain' },
      { label: 'Testicular Pain' },
    ],
  },
  {
    category: 'Respiratory',
    options: [
      { label: 'Shortness of Breath', aliases: ['SOB', 'Dyspnea'] },
      { label: 'Difficulty Breathing' },
      { label: 'Wheezing (Patient-Reported)' },
      { label: 'Cough' },
      { label: 'Coughing Up Blood' },
      { label: 'Chest Tightness' },
      { label: 'Choking Sensation' },
    ],
  },
  {
    category: 'Cardiovascular',
    options: [
      { label: 'Palpitations' },
      { label: 'Racing Heart' },
      { label: 'Lightheadedness' },
      { label: 'Near-Syncope' },
      { label: 'Loss of Consciousness' },
    ],
  },
  {
    category: 'Neurological',
    options: [
      { label: 'Dizziness' },
      { label: 'Vertigo' },
      { label: 'Weakness' },
      { label: 'Numbness' },
      { label: 'Tingling / Paresthesia' },
      { label: 'Confusion' },
      { label: 'Memory Loss' },
      { label: 'Vision Changes' },
      { label: 'Double Vision' },
      { label: 'Loss of Balance' },
      { label: 'Difficulty Speaking' },
      { label: 'Difficulty Swallowing' },
    ],
  },
  {
    category: 'GI',
    options: [
      { label: 'Nausea' },
      { label: 'Vomiting' },
      { label: 'Diarrhea' },
      { label: 'Constipation' },
      { label: 'Abdominal Cramping' },
      { label: 'Heartburn' },
      { label: 'Loss of Appetite' },
      { label: 'Bloating' },
    ],
  },
  {
    category: 'General',
    options: [
      { label: 'Fever' },
      { label: 'Chills' },
      { label: 'Fatigue' },
      { label: 'Generalized Weakness' },
      { label: 'Malaise' },
      { label: 'Night Sweats' },
      { label: 'Weight Loss (Unintentional)' },
      { label: 'Itching / Pruritus' },
      { label: 'Rash (Patient-Reported)' },
    ],
  },
  {
    category: 'Psychiatric',
    options: [
      { label: 'Anxiety' },
      { label: 'Depression' },
      { label: 'Suicidal Thoughts' },
      { label: 'Homicidal Thoughts' },
      { label: 'Hallucinations (Visual)' },
      { label: 'Hallucinations (Auditory)' },
      { label: 'Panic' },
      { label: 'Agitation' },
    ],
  },
];

// ============================================================
// Helpers
// ============================================================

/** Flatten a grouped list into a flat ClinicalOption[] with category stamped in. */
export function flattenGroups(groups: ClinicalGroup[]): Array<ClinicalOption & { category: string }> {
  return groups.flatMap((g) => g.options.map((o) => ({ ...o, category: g.category })));
}

/** Case-insensitive match helper for SearchableSelect — matches label or any alias. */
export function matchesQuery(option: ClinicalOption, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (option.label.toLowerCase().includes(q)) return true;
  if (option.aliases?.some((a) => a.toLowerCase().includes(q))) return true;
  return false;
}
